// Payments/PaymentLinksTable.js

import React, { useState, useEffect } from 'react';
import axiosInstance from "../../../utils/axiosInstance";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TablePagination, CircularProgress, Alert, Box, Chip, IconButton, Tooltip,
  Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Switch, FormControlLabel, Typography, Card, CardContent,
  Stack, alpha, useTheme, Grid, Snackbar, ListItemIcon, ListItemText,
  Divider
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import BarChartIcon from '@mui/icons-material/BarChart';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LinkIcon from '@mui/icons-material/Link';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';

const FONT = "'Plus Jakarta Sans', sans-serif";

// Shared header cell style
const headerCellSx = (theme) => ({
  fontFamily: FONT,
  fontWeight: 700,
  fontSize: '0.72rem',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: theme.palette.text.secondary,
  bgcolor: alpha(theme.palette.text.primary, 0.02),
  borderBottom: `1px solid ${theme.palette.divider}`,
  py: 1.5,
  whiteSpace: 'nowrap',
});

// Shared body cell style
const bodyCellSx = {
  fontFamily: FONT,
  fontSize: '0.85rem',
  py: 1.5,
};

const PaymentLinksTable = ({ refreshKey, onRefresh, permission }) => {
  const theme = useTheme();
  const [links, setLinks] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedLink, setSelectedLink] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [statsDialog, setStatsDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  useEffect(() => {
    fetchLinks();
  }, [page, rowsPerPage, refreshKey]);

  const fetchLinks = async () => {
    setIsLoading(true);
    try {
      const params = { page: page + 1, per_page: rowsPerPage };
      const response = await axiosInstance.get(`/payments/payment-links`, { params });
      setLinks(response.data.data);
      setTotalRows(response.data.total);
      setError('');
    } catch (err) {
      setError('Failed to fetch payment links.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMenuClick = (event, link) => {
    setAnchorEl(event.currentTarget);
    setSelectedLink(link);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCopyLink = () => {
    if (selectedLink) {
      const link = `${window.location.origin}/pay/${selectedLink.token}`;
      navigator.clipboard.writeText(link).then(() => {
        setSnackbar({ open: true, message: 'Payment link copied to clipboard' });
      });
    }
    handleMenuClose();
  };

  const handleToggleStatus = async () => {
    try {
      await axiosInstance.patch(
        `/payments/payment-links/${selectedLink.id}/toggle-status`,
        {}
      );
      fetchLinks();
      if (onRefresh) onRefresh();
      setSnackbar({
        open: true,
        message: `Link ${selectedLink?.status === 'active' ? 'deactivated' : 'activated'} successfully`,
      });
    } catch (err) {
      setError('Failed to update link status.');
    }
    handleMenuClose();
  };

  const handleDeleteLink = async () => {
    try {
      await axiosInstance.delete(`/payments/payment-links/${selectedLink.id}`);
      fetchLinks();
      if (onRefresh) onRefresh();
      setDeleteDialog(false);
      setSnackbar({ open: true, message: 'Payment link deleted' });
    } catch (err) {
      setError('Failed to delete payment link.');
    }
    handleMenuClose();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // -- Status Chip --
  const getStatusChip = (status) => {
    const map = {
      active: {
        label: 'Active',
        color: theme.palette.success.main,
        bg: alpha(theme.palette.success.main, 0.08),
      },
      inactive: {
        label: 'Inactive',
        color: theme.palette.text.disabled,
        bg: alpha(theme.palette.text.disabled, 0.08),
      },
      expired: {
        label: 'Expired',
        color: theme.palette.error.main,
        bg: alpha(theme.palette.error.main, 0.08),
      },
    };
    const cfg = map[status] || map.inactive;
    return (
      <Chip
        size="small"
        label={cfg.label}
        sx={{
          fontFamily: FONT,
          fontWeight: 600,
          fontSize: '0.7rem',
          height: 24,
          borderRadius: 1.5,
          color: cfg.color,
          bgcolor: cfg.bg,
          border: 'none',
        }}
      />
    );
  };

  // -- Type Chip --
  const getPaymentTypeChip = (type) => {
    const map = {
      donation: { label: 'Donation', color: theme.palette.primary.main },
      signup: { label: 'Signup', color: theme.palette.secondary.main },
      subscription: { label: 'Subscription', color: theme.palette.info.main },
      product: { label: 'Product', color: theme.palette.warning.main },
      others: { label: 'Others', color: theme.palette.text.secondary },
    };
    const cfg = map[type] || map.others;
    return (
      <Chip
        size="small"
        label={cfg.label}
        sx={{
          fontFamily: FONT,
          fontWeight: 600,
          fontSize: '0.7rem',
          height: 24,
          borderRadius: 1.5,
          color: cfg.color,
          bgcolor: alpha(cfg.color, 0.08),
          border: 'none',
        }}
      />
    );
  };

  // -- Loading --
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
        <CircularProgress size={28} thickness={4} />
        <Typography
          sx={{ mt: 2, fontFamily: FONT, fontSize: '0.85rem', color: 'text.secondary' }}
        >
          Loading payment links...
        </Typography>
      </Box>
    );
  }

  // -- Error --
  if (error) {
    return (
      <Alert
        severity="error"
        sx={{
          m: 2,
          borderRadius: 2,
          fontFamily: FONT,
          border: `1px solid ${alpha(theme.palette.error.main, 0.15)}`,
        }}
      >
        {error}
      </Alert>
    );
  }

  // -- Empty --
  if (links.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 3,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <LinkOffIcon sx={{ fontSize: 28, color: theme.palette.primary.main }} />
        </Box>
        <Typography
          sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '1rem', color: 'text.primary', mb: 0.5 }}
        >
          No payment links yet
        </Typography>
        <Typography
          sx={{ fontFamily: FONT, fontSize: '0.85rem', color: 'text.secondary' }}
        >
          Create your first payment link to get started
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <TableContainer
        sx={{
          maxHeight: 600,
          overflowX: 'auto',
          width: '100%',
          scrollbarWidth: 'thin',
          scrollbarColor: `${theme.palette.divider} transparent`,
          '&::-webkit-scrollbar': { width: 6, height: 6 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: theme.palette.divider,
            borderRadius: 3,
          },
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellSx(theme), minWidth: 200 }}>Title</TableCell>
              <TableCell sx={{ ...headerCellSx(theme), minWidth: 100 }}>Type</TableCell>
              <TableCell sx={{ ...headerCellSx(theme), minWidth: 90 }}>Status</TableCell>
              <TableCell sx={{ ...headerCellSx(theme), minWidth: 160 }}>Methods</TableCell>
              <TableCell sx={{ ...headerCellSx(theme), minWidth: 100 }}>Source</TableCell>
              <TableCell sx={{ ...headerCellSx(theme), minWidth: 100 }} align="right">Revenue</TableCell>
              <TableCell sx={{ ...headerCellSx(theme), minWidth: 80 }} align="right">Views</TableCell>
              <TableCell sx={{ ...headerCellSx(theme), minWidth: 100 }}>Created</TableCell>
              <TableCell sx={{ ...headerCellSx(theme), minWidth: 60 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {links.map((link) => (
              <TableRow
                hover
                key={link.id}
                sx={{
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.015) },
                  '&:last-child td': { borderBottom: 0 },
                }}
              >
                {/* Title */}
                <TableCell sx={{ ...bodyCellSx }}>
                  <Box sx={{ maxWidth: 260 }}>
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        color: 'text.primary',
                        lineHeight: 1.3,
                      }}
                      noWrap
                    >
                      {link.title}
                    </Typography>
                    {link.description && (
                      <Typography
                        sx={{
                          fontFamily: FONT,
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                          mt: 0.25,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {link.description.length > 55
                          ? `${link.description.substring(0, 55)}...`
                          : link.description}
                      </Typography>
                    )}
                    {/* Token with copy */}
                    {link.token && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Typography
                          sx={{
                            fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                            fontSize: '0.65rem',
                            color: 'text.disabled',
                            bgcolor: alpha(theme.palette.text.primary, 0.03),
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 1,
                            maxWidth: 140,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {link.token}
                        </Typography>
                        <Tooltip title="Copy link" arrow>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const url = `${window.location.origin}/pay/${link.token}`;
                              navigator.clipboard.writeText(url).then(() => {
                                setSnackbar({ open: true, message: 'Link copied' });
                              });
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <ContentCopyIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                </TableCell>

                {/* Type */}
                <TableCell sx={{ ...bodyCellSx }}>
                  {getPaymentTypeChip(link.payment_type)}
                </TableCell>

                {/* Status */}
                <TableCell sx={{ ...bodyCellSx }}>
                  {getStatusChip(link.status)}
                </TableCell>

                {/* Methods */}
                <TableCell sx={{ ...bodyCellSx }}>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {link.payment_methods?.stripe && (
                      <Chip
                        size="small"
                        icon={<CreditCardIcon sx={{ fontSize: 13 }} />}
                        label="Card"
                        sx={{
                          fontFamily: FONT,
                          fontWeight: 600,
                          fontSize: '0.65rem',
                          height: 22,
                          borderRadius: 1.5,
                          bgcolor: alpha('#1565c0', 0.08),
                          color: '#1565c0',
                          border: 'none',
                          '& .MuiChip-icon': { color: '#1565c0' },
                        }}
                      />
                    )}
                    {link.payment_methods?.paypal && (
                      <Chip
                        size="small"
                        label="PayPal"
                        sx={{
                          fontFamily: FONT,
                          fontWeight: 600,
                          fontSize: '0.65rem',
                          height: 22,
                          borderRadius: 1.5,
                          bgcolor: alpha('#283593', 0.08),
                          color: '#283593',
                          border: 'none',
                        }}
                      />
                    )}
                    {link.payment_methods?.coinbase && (
                      <Chip
                        size="small"
                        icon={<CurrencyBitcoinIcon sx={{ fontSize: 13 }} />}
                        label={link.crypto_token ? `Crypto (${link.crypto_token})` : 'Crypto'}
                        sx={{
                          fontFamily: FONT,
                          fontWeight: 600,
                          fontSize: '0.65rem',
                          height: 22,
                          borderRadius: 1.5,
                          bgcolor: alpha('#e65100', 0.08),
                          color: '#e65100',
                          border: 'none',
                          '& .MuiChip-icon': { color: '#e65100' },
                        }}
                      />
                    )}
                  </Stack>
                </TableCell>

                {/* Source */}
                <TableCell sx={{ ...bodyCellSx }}>
                  {link.source && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={link.source.charAt(0).toUpperCase() + link.source.slice(1)}
                      sx={{
                        fontFamily: FONT,
                        fontWeight: 600,
                        fontSize: '0.65rem',
                        height: 22,
                        borderRadius: 1.5,
                        borderColor: theme.palette.divider,
                        color: 'text.secondary',
                      }}
                    />
                  )}
                </TableCell>

                {/* Revenue */}
                <TableCell sx={{ ...bodyCellSx }} align="right">
                  <Typography
                    sx={{
                      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                      fontFeatureSettings: '"tnum"',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      color: 'text.primary',
                    }}
                  >
                    ${link.total_revenue || '0.00'}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontSize: '0.7rem',
                      color: 'text.disabled',
                    }}
                  >
                    {link.payment_count || 0} payment{(link.payment_count || 0) !== 1 ? 's' : ''}
                  </Typography>
                </TableCell>

                {/* Views */}
                <TableCell sx={{ ...bodyCellSx }} align="right">
                  <Typography
                    sx={{
                      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                      fontFeatureSettings: '"tnum"',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      color: 'text.secondary',
                    }}
                  >
                    {link.view_count || 0}
                  </Typography>
                </TableCell>

                {/* Created */}
                <TableCell sx={{ ...bodyCellSx }}>
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontSize: '0.8rem',
                      color: 'text.secondary',
                    }}
                    noWrap
                  >
                    {new Date(link.created_date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </Typography>
                </TableCell>

                {/* Actions */}
                <TableCell sx={{ ...bodyCellSx }} align="center">
                  <IconButton
                    onClick={(event) => handleMenuClick(event, link)}
                    size="small"
                    sx={{
                      color: 'text.secondary',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.06),
                        color: theme.palette.primary.main,
                      },
                    }}
                  >
                    <MoreVertIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={totalRows}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          borderTop: `1px solid ${theme.palette.divider}`,
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            fontFamily: FONT,
            fontSize: '0.8rem',
          },
        }}
      />

      {/* ========== Actions Menu ========== */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2.5,
            mt: 0.5,
            minWidth: 180,
            boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
            '& .MuiMenuItem-root': {
              fontFamily: FONT,
              fontSize: '0.82rem',
              py: 1,
              px: 2,
              borderRadius: 1.5,
              mx: 0.5,
              mb: 0.25,
              gap: 1.5,
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.05),
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleCopyLink}>
          <ListItemIcon sx={{ minWidth: '28px !important' }}>
            <ContentCopyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontFamily: FONT, fontSize: '0.82rem' }}>
            Copy Link
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setStatsDialog(true); handleMenuClose(); }}>
          <ListItemIcon sx={{ minWidth: '28px !important' }}>
            <BarChartIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontFamily: FONT, fontSize: '0.82rem' }}>
            View Stats
          </ListItemText>
        </MenuItem>

        {permission.canEdit && (
          <Box>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem onClick={() => { setEditDialog(true); handleMenuClose(); }}>
              <ListItemIcon sx={{ minWidth: '28px !important' }}>
                <EditIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontFamily: FONT, fontSize: '0.82rem' }}>
                Edit
              </ListItemText>
            </MenuItem>
            <MenuItem onClick={handleToggleStatus}>
              <ListItemIcon sx={{ minWidth: '28px !important' }}>
                {selectedLink?.status === 'active' ? (
                  <VisibilityOffIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                ) : (
                  <VisibilityIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                )}
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontFamily: FONT, fontSize: '0.82rem' }}>
                {selectedLink?.status === 'active' ? 'Deactivate' : 'Activate'}
              </ListItemText>
            </MenuItem>
          </Box>
        )}

        {permission.canDelete && (
          <Box>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem
              onClick={() => { setDeleteDialog(true); handleMenuClose(); }}
              sx={{
                '&:hover': {
                  bgcolor: `${alpha(theme.palette.error.main, 0.06)} !important`,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: '28px !important' }}>
                <DeleteIcon sx={{ fontSize: 16, color: theme.palette.error.main }} />
              </ListItemIcon>
              <ListItemText
                primaryTypographyProps={{
                  fontFamily: FONT,
                  fontSize: '0.82rem',
                  color: theme.palette.error.main,
                }}
              >
                Delete
              </ListItemText>
            </MenuItem>
          </Box>
        )}
      </Menu>

      {/* ========== Stats Dialog ========== */}
      <Dialog
        open={statsDialog}
        onClose={() => setStatsDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: FONT,
            fontWeight: 700,
            fontSize: '1.05rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
          }}
        >
          Payment Link Statistics
          <IconButton size="small" onClick={() => setStatsDialog(false)}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {selectedLink && <PaymentLinkStats link={selectedLink} />}
        </DialogContent>
      </Dialog>

      {/* ========== Edit Dialog ========== */}
      {permission.canEdit && (
        <Dialog
          open={editDialog}
          onClose={() => setEditDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            elevation: 0,
            sx: {
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          <DialogTitle
            sx={{
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: '1.05rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pb: 1,
            }}
          >
            Edit Payment Link
            <IconButton size="small" onClick={() => setEditDialog(false)}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            {selectedLink && (
              <EditPaymentLinkForm
                link={selectedLink}
                onSave={() => {
                  setEditDialog(false);
                  fetchLinks();
                  if (onRefresh) onRefresh();
                  setSnackbar({ open: true, message: 'Payment link updated' });
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* ========== Delete Confirmation Dialog ========== */}
      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: FONT,
            fontWeight: 700,
            fontSize: '1.05rem',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <WarningAmberIcon sx={{ color: theme.palette.error.main, fontSize: 22 }} />
          Delete Payment Link
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: FONT, fontSize: '0.9rem', color: 'text.secondary' }}>
            Are you sure you want to delete <strong>"{selectedLink?.title}"</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialog(false)}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontFamily: FONT,
              color: 'text.secondary',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteLink}
            variant="contained"
            color="error"
            disableElevation
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontFamily: FONT,
            }}
          >
            Delete Link
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== Snackbar Toast ========== */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            fontFamily: FONT,
            fontSize: '0.85rem',
            borderRadius: 2,
            bgcolor: theme.palette.text.primary,
          },
        }}
      />
    </Box>
  );
};

// ====================================
// Stats Component
// ====================================
const PaymentLinkStats = ({ link }) => {
  const theme = useTheme();
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axiosInstance.get(
          `/payments/payment-links/${link.id}/stats`
        );
        setStats(response.data.stats);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, [link.id]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={28} thickness={4} />
      </Box>
    );
  }

  const statCards = [
    {
      label: 'Total Views',
      value: stats?.total_views || 0,
      icon: <RemoveRedEyeOutlinedIcon sx={{ fontSize: 20 }} />,
      color: theme.palette.primary.main,
    },
    {
      label: 'Total Payments',
      value: stats?.total_payments || 0,
      icon: <PaymentsOutlinedIcon sx={{ fontSize: 20 }} />,
      color: theme.palette.success.main,
    },
    {
      label: 'Total Revenue',
      value: `$${stats?.total_revenue || '0.00'}`,
      icon: <TrendingUpOutlinedIcon sx={{ fontSize: 20 }} />,
      color: theme.palette.secondary.main,
      mono: true,
    },
    {
      label: 'Conversion Rate',
      value: `${stats?.conversion_rate || '0'}%`,
      icon: <PercentOutlinedIcon sx={{ fontSize: 20 }} />,
      color: theme.palette.info.main,
    },
  ];

  return (
    <Box>
      <Typography
        sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '1rem', mb: 2.5, color: 'text.primary' }}
      >
        {link.title}
      </Typography>

      {/* Stat Cards Grid */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((card) => (
          <Grid item xs={6} md={3} key={card.label}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: theme.palette.background.paper,
              }}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    bgcolor: alpha(card.color, 0.08),
                    color: card.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1.5,
                  }}
                >
                  {card.icon}
                </Box>
                <Typography
                  sx={{
                    fontFamily: card.mono
                      ? "'JetBrains Mono', 'Fira Code', monospace"
                      : FONT,
                    fontFeatureSettings: card.mono ? '"tnum"' : undefined,
                    fontWeight: 700,
                    fontSize: '1.35rem',
                    color: 'text.primary',
                    lineHeight: 1.2,
                  }}
                >
                  {card.value}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontSize: '0.72rem',
                    fontWeight: 500,
                    color: 'text.secondary',
                    mt: 0.25,
                    textTransform: 'uppercase',
                    letterSpacing: 0.3,
                  }}
                >
                  {card.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Recent Payments */}
      {stats?.recent_payments && stats.recent_payments.length > 0 && (
        <>
          <Typography
            sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.9rem', mb: 1.5, color: 'text.primary' }}
          >
            Recent Payments
          </Typography>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ borderRadius: 2.5, border: `1px solid ${theme.palette.divider}` }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx(theme) }}>Date</TableCell>
                  <TableCell sx={{ ...headerCellSx(theme) }}>Customer</TableCell>
                  <TableCell sx={{ ...headerCellSx(theme) }} align="right">Amount</TableCell>
                  <TableCell sx={{ ...headerCellSx(theme) }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.recent_payments.map((payment, index) => (
                  <TableRow
                    key={index}
                    sx={{ '&:last-child td': { borderBottom: 0 } }}
                  >
                    <TableCell sx={{ ...bodyCellSx, fontSize: '0.8rem', color: 'text.secondary' }}>
                      {new Date(payment.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, fontSize: '0.8rem' }}>
                      {payment.customer_name || payment.customer_email}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...bodyCellSx,
                        fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                        fontFeatureSettings: '"tnum"',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                      }}
                    >
                      ${payment.amount}
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx }}>
                      <Chip
                        size="small"
                        label={payment.status}
                        sx={{
                          fontFamily: FONT,
                          fontWeight: 600,
                          fontSize: '0.65rem',
                          height: 22,
                          borderRadius: 1.5,
                          bgcolor: alpha(theme.palette.success.main, 0.08),
                          color: theme.palette.success.main,
                          border: 'none',
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

// ====================================
// Edit Form Component
// ====================================
const EditPaymentLinkForm = ({ link, onSave }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    title: link.title,
    description: link.description,
    is_active: link.status === 'active',
    expires_at: link.expires_at || '',
  });
  const [saving, setSaving] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axiosInstance.patch(
        `/payments/payment-links/${link.id}`,
        formData
      );
      onSave();
    } catch (err) {
      console.error('Failed to update payment link:', err);
    } finally {
      setSaving(false);
    }
  };

  const inputSx = {
    '& .MuiInputBase-root': {
      fontFamily: FONT,
      fontSize: '0.9rem',
      borderRadius: 2,
    },
    '& .MuiInputLabel-root': {
      fontFamily: FONT,
      fontSize: '0.9rem',
    },
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <TextField
        fullWidth
        label="Title"
        name="title"
        value={formData.title}
        onChange={handleInputChange}
        sx={{ ...inputSx, mb: 2.5 }}
        required
      />
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Description"
        name="description"
        value={formData.description || ''}
        onChange={handleInputChange}
        sx={{ ...inputSx, mb: 2.5 }}
      />
      <TextField
        fullWidth
        type="datetime-local"
        label="Expires At"
        name="expires_at"
        value={formData.expires_at}
        onChange={handleInputChange}
        InputLabelProps={{ shrink: true }}
        sx={{ ...inputSx, mb: 2.5 }}
      />
      <FormControlLabel
        control={
          <Switch
            checked={formData.is_active}
            onChange={handleInputChange}
            name="is_active"
            color="success"
          />
        }
        label={
          <Typography sx={{ fontFamily: FONT, fontSize: '0.9rem', fontWeight: 500 }}>
            Active
          </Typography>
        }
        sx={{ mb: 3, ml: 0 }}
      />
      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        disabled={saving}
        disableElevation
        sx={{
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          fontFamily: FONT,
          py: 1.25,
        }}
      >
        {saving ? 'Saving...' : 'Save Changes'}
      </Button>
    </Box>
  );
};

export default PaymentLinksTable;
